import React from 'react';

import {getArchiveItemName} from 'archive/ArchivePurposesUtil';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isRegistrySupportedType, TagSchema} from 'constants/RegistryConstants';
import {
  ChildEntitiesLookupStepSpec,
  LocationLookupStepSpec,
  LookupConfigModalContextInfo,
} from 'lookup-config/LookupConfigModalConstants';
import {getItemTypeForResultsStep, getSchemaFieldsForLookupStep} from 'lookup-config/LookupConfigUtils';
import {ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import {MeasurementType} from 'samples/MeasurementForm';
import isSchemaFieldArchived, {getSchemaFieldArchivableItem} from 'schemas/fields/isSchemaFieldArchived';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {
  getTagSchemaTypeName,
  isBioentityLinkFieldType,
  isStorableLinkFieldType,
} from 'tag-schema/util/TagSchemaUtil';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import {Optional} from 'util/Types';

import {
  getAllBoxSchemasFromCache,
  getAllContainerSchemasFromCache,
  getAllLocationSchemasFromCache,
  getAllPlateSchemasFromCache,
  getAllResultSchemasFromCache,
  getAllTagSchemasFromCache,
} from './AllSchemasQueries';
import DropdownValueForm, {OptionValue} from './DropdownValueForm';
import {
  BoxContainersLookupStepSpec,
  BoxLookupStepSpec,
  ConcentrationLookupStepSpec,
  ConstantLookupStepSpec,
  ContainerByGridPositionLookupStepSpec,
  ContainerLookupStepSpec,
  ContentsLookupStepSpec,
  CoordinateType,
  CountLookupStepSpec,
  FillDirection,
  FormErrors,
  LookupStepInfo,
  LookupStepSpec,
  LookupStepType,
  PlaceholderBoxesLookupStepSpec,
  PlaceholderPlatesLookupStepSpec,
  PlateLookupStepSpec,
  QuantityLookupStepSpec,
  ResultsLookupStepSpec,
  TaskFinderProcessTasksLookupStepSpec,
  WellCoordinatesLookupStepSpec,
  WellsLookupStepSpec,
} from './LookupConfigModalConstants';
import MeasurementUnitForm from './MeasurementUnitForm';
import {getLookupFormErrorMessage} from './utils/getFormErrors';
import WellsLookupStepConfigForm from './WellsLookupStepConfigForm';

const FILL_DIRECTIONS_AND_COORDINATE_TYPES_TO_VALUES = [
  {
    text: 'Alphanumeric ("A1")',
    value: CoordinateType.LETTER_AND_NUMBER,
  },
  {
    text: '2-digit alphanumeric ("A01")',
    value: CoordinateType.LETTER_AND_TWO_DIGIT_NUMBER,
  },
  {text: 'Numeric, ordered across rows', value: FillDirection.ACROSS_ROWS},
  {text: 'Numeric, ordered down columns', value: FillDirection.DOWN_COLUMNS},
  {
    text: 'Row letters only ("A")',
    value: CoordinateType.LETTER_ONLY,
  },
  {
    text: 'Column numbers only ("1")',
    value: CoordinateType.NUMBER_ONLY,
  },
];

export const getDropdownValueFormTestAttrs = (label: string): DataTestAttrs => ({
  component: 'DropdownValueForm',
  key: label,
});

type boundGetErrorFn = (
  errorText: string,
  errors: FormErrors,
  errorKey: keyof FormErrors
) => string | JSX.Element | null;

interface DummyFormProps {
  disabled?: boolean;
  isInline?: boolean;
  lookupInfo: LookupStepInfo;
  onChange: (lookup: LookupStepSpec, linkSchema: Optional<Schema>) => void;
  formErrors: FormErrors;
  getErrorMessage: boundGetErrorFn;
}

function getSchemaDropdownOptions(
  emptyLabel: string | null,
  schemas: Array<Schema>,
  selectedSchema: Schema | null
): Array<OptionValue> {
  const options = [];
  if (emptyLabel != null) {
    options.push({text: emptyLabel, value: undefined});
  }
  if (selectedSchema && isArchived(selectedSchema)) {
    const schemaName = getArchiveItemName({
      ...selectedSchema,
    });
    options.push({
      value: selectedSchema.id,
      text: schemaName,
    });
  }
  options.push(
    ...schemas
      .filter((schema) => !isArchived(schema))
      .map((schema: Schema) => ({
        value: schema.id,
        text: schema.name,
      }))
  );
  return options;
}

const _inventorySchemaStepConfigForm = <T extends LookupStepSpec>(
  props: DummyFormProps,
  label: string,
  lookupStepKey: keyof T,
  schemas: Array<Schema>,
  emptyLabel: string | null = null
): JSX.Element => {
  const selectedSchema = props.lookupInfo.linkSchema;
  const options = getSchemaDropdownOptions(emptyLabel, schemas, selectedSchema);
  const formError = props.getErrorMessage('Schema deleted', props.formErrors, 'dropdownValue');
  return (
    <DropdownValueForm
      disabled={props.disabled}
      label={label}
      isInline={props.isInline}
      isRequired={emptyLabel == null}
      value={(props.lookupInfo.lookupStep as T)[lookupStepKey]}
      onUpdate={(value?: string) => {
        props.onChange(
          {
            ...props.lookupInfo.lookupStep,
            [lookupStepKey]: value,
          },
          schemas.find((schema) => schema.id === value)
        );
      }}
      options={options}
      dataTestAttrs={getDropdownValueFormTestAttrs(lookupStepKey as string)}
      error={formError}
    />
  );
};

const _registrySchemaStepConfigForm = (
  props: DummyFormProps,
  label: string,
  tagSchemas: Array<TagSchema>,
  emptyLabel: string | null = null,
  selectionPlaceholder?: string
): JSX.Element => {
  const value =
    (props.lookupInfo.lookupStep as ContentsLookupStepSpec).entitySchema ??
    (props.lookupInfo.lookupStep as ContentsLookupStepSpec).entitySchemaType;

  const selectedSchema = props.lookupInfo.linkSchema;
  const options = getSchemaDropdownOptions(null, tagSchemas, selectedSchema);

  const extraOptions = [];

  // Handle empty label separately to avoid extra slice operation required to insert bioentity type options in
  // between the empty label and the schemas
  if (emptyLabel != null) {
    extraOptions.push({text: emptyLabel, value: undefined});
  }
  extraOptions.push(
    ...[
      {
        value: ModelTypeConstants.BASIC_FOLDER_ITEM,
        text: 'Any '.concat(getTagSchemaTypeName({folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM})),
      },
      {
        value: ModelTypeConstants.MIXTURE,
        text: 'Any '.concat(getTagSchemaTypeName({folderItemType: ModelTypeConstants.MIXTURE})),
      },
      {
        value: ModelTypeConstants.PROTEIN,
        text: 'Any '.concat(getTagSchemaTypeName({folderItemType: ModelTypeConstants.PROTEIN})),
      },
      {
        value: ModelTypeConstants.SEQUENCE,
        text: 'Any '.concat(getTagSchemaTypeName({folderItemType: ModelTypeConstants.SEQUENCE})),
      },
    ]
  );

  options.unshift(...extraOptions);

  const formError = props.getErrorMessage(
    'Must select a target schema or bioentity type',
    props.formErrors,
    'dropdownValue'
  );

  return (
    <DropdownValueForm
      disabled={props.disabled}
      label={label}
      isInline={props.isInline}
      isRequired={emptyLabel == null}
      value={value}
      options={options}
      dataTestAttrs={getDropdownValueFormTestAttrs('entitySchema')}
      onUpdate={(value?: string) => {
        if (value && isRegistrySupportedType(value)) {
          props.onChange(
            {
              ...(props.lookupInfo.lookupStep as ContentsLookupStepSpec),
              entitySchemaType: value,
              entitySchema: undefined,
            },
            null
          );
        } else {
          props.onChange(
            {
              ...(props.lookupInfo.lookupStep as ContentsLookupStepSpec),
              entitySchema: value,
              entitySchemaType: undefined,
            },
            tagSchemas.find((schema) => schema.id === value)
          );
        }
      }}
      error={formError}
      selectionPlaceholder={selectionPlaceholder}
    />
  );
};

const _schemasDropdownForm = <T extends LookupStepSpec>(
  props: DummyFormProps,
  label: string,
  lookupStepKey: keyof T,
  schemas: Array<TagSchema>,
  emptyLabel: string | null = null
): JSX.Element => {
  const options = [];

  if (emptyLabel != null) {
    options.push({text: emptyLabel, value: undefined});
  }
  options.push(
    ...Object.values(schemas)
      .filter((schema) => schema && !isArchived(schema))
      .map((schema: TagSchema) => ({
        value: schema.id,
        text: schema.name,
      }))
  );

  return (
    <DropdownValueForm
      dataTestAttrs={getDropdownValueFormTestAttrs(lookupStepKey as string)}
      disabled={props.disabled}
      label={label}
      isInline={props.isInline}
      isRequired={false}
      value={(props.lookupInfo.lookupStep as T)[lookupStepKey]}
      options={options}
      onUpdate={(value?: string) => {
        props.onChange(
          {
            ...props.lookupInfo.lookupStep,
            [lookupStepKey]: value,
          },
          schemas.find((schema) => schema.id === value)
        );
      }}
    />
  );
};

const _childEntitySchemaStepConfigForm = (
  props: DummyFormProps,
  label: string,
  parentSchema: TagSchema | null
): JSX.Element => {
  const childEntitySchemaIds =
    parentSchema && parentSchema.childEntitySchemaSummaries
      ? new Set(parentSchema.childEntitySchemaSummaries.map((summary) => summary.id))
      : new Set();
  const tagSchemas = getAllTagSchemasFromCache();
  const childEntitySchemas = tagSchemas.filter((tagSchema) => childEntitySchemaIds.has(tagSchema.id));
  return _schemasDropdownForm<ChildEntitiesLookupStepSpec>(
    props,
    label,
    'entitySchema',
    childEntitySchemas,
    'Any Entity'
  );
};

const _containableSchemaStepConfigForm = (props: DummyFormProps, label: string): JSX.Element => {
  const tagSchemas = getAllTagSchemasFromCache().filter((schema) => schema.isContainable);
  return _registrySchemaStepConfigForm(props, label, tagSchemas, 'Any Containable Entity');
};

const _boxSchemaStepConfigForm = (
  props: DummyFormProps,
  label: string,
  allowEmpty: boolean = false
): JSX.Element => {
  type BoxStep = BoxLookupStepSpec | PlaceholderBoxesLookupStepSpec;
  const boxSchemas = getAllBoxSchemasFromCache();
  return _inventorySchemaStepConfigForm<BoxStep>(
    props,
    label,
    'boxSchema',
    boxSchemas,
    allowEmpty ? 'Any Box' : null
  );
};

const _containerSchemaStepConfigForm = (props: DummyFormProps, label: string): JSX.Element => {
  const containerSchemas = getAllContainerSchemasFromCache();
  return _inventorySchemaStepConfigForm<ContainerLookupStepSpec>(
    props,
    label,
    'containerSchema',
    containerSchemas,
    'Any Container'
  );
};

const _locationSchemaStepConfigForm = (props: DummyFormProps, label: string): JSX.Element => {
  const locationSchemas = getAllLocationSchemasFromCache();
  return _inventorySchemaStepConfigForm<LocationLookupStepSpec>(
    props,
    label,
    'locationSchema',
    locationSchemas,
    'Any Location'
  );
};

const _plateSchemaStepConfigForm = (
  props: DummyFormProps,
  label: string,
  allowEmpty: boolean = false
): JSX.Element => {
  type PlateStep = PlateLookupStepSpec | PlaceholderPlatesLookupStepSpec;
  const plateSchemas = getAllPlateSchemasFromCache();
  return _inventorySchemaStepConfigForm<PlateStep>(
    props,
    label,
    'plateSchema',
    plateSchemas,
    allowEmpty ? 'Any Plate' : null
  );
};

const _resultsSchemaStepConfigForm = (
  props: DummyFormProps,
  itemType: string,
  schemaFieldFilterFn?: (field: SchemaField, excludeNonLinkFields: boolean) => boolean
): JSX.Element => {
  const fieldOptions = [];
  const nonarchivedResultSchemas = getAllResultSchemasFromCache().filter((schema) => !isArchived(schema));
  const filterSchemaFieldLinkTypeFn =
    itemType === 'entity' ? isBioentityLinkFieldType : isStorableLinkFieldType;
  const resultSchemasToLinkFields = nonarchivedResultSchemas.map((resultSchema) => ({
    resultSchema,
    resultSchemaFields: resultSchema?.allFields.filter(
      (field: SchemaField) =>
        filterSchemaFieldLinkTypeFn(field.fieldType) &&
        (schemaFieldFilterFn ? schemaFieldFilterFn(field, true) : true) &&
        field.archiveRecord == null
    ),
  }));
  const resultSchemasByFieldId: {[fieldId: string]: ResultSchema} = {};
  for (const {resultSchemaFields = [], resultSchema} of resultSchemasToLinkFields) {
    for (const field of resultSchemaFields) {
      fieldOptions.push({text: `${resultSchema?.name} / ${field.displayName}`, value: field.apiId});
      resultSchemasByFieldId[field.apiId] = resultSchema;
    }
  }

  const formError = props.getErrorMessage('Field deleted', props.formErrors, 'dropdownValue');

  return (
    <DropdownValueForm
      disabled={props.disabled}
      isRequired={true}
      isInline={props.isInline}
      label={`Results of ${itemType}`}
      value={(props.lookupInfo.lookupStep as ResultsLookupStepSpec).linkedBySchemaField}
      onUpdate={(value: string) => {
        props.onChange(
          {
            ...(props.lookupInfo.lookupStep as ResultsLookupStepSpec),
            linkedBySchemaField: value,
          },
          resultSchemasByFieldId[value]
        );
      }}
      options={fieldOptions}
      dataTestAttrs={getDropdownValueFormTestAttrs('linkedBySchemaField')}
      error={formError}
      tooltipText="Select the Result schema and field name that contain the value of your previous lookup step."
    />
  );
};

export const lookupConfigStepForm = (
  context: LookupConfigModalContextInfo | undefined,
  allowAlternatingGridPatterns: boolean,
  shouldShowNullForEmptyContentsTooltip: boolean,
  schemaFieldFilterFn: ((field: SchemaField, excludeNonLinkFields: boolean) => boolean) | undefined,
  isFirstStep: boolean,
  previousStepInfo: LookupStepInfo | null
): {
  [type in LookupStepType]?: (props: DummyFormProps) => JSX.Element;
} => ({
  [LookupStepType.BOX]: (props: DummyFormProps) => _boxSchemaStepConfigForm(props, 'Box of container', true),
  [LookupStepType.BOX_CONTAINERS]: (props: DummyFormProps) => (
    <WellsLookupStepConfigForm
      {...props}
      lookupInfo={props.lookupInfo as LookupStepInfo & {lookupStep: BoxContainersLookupStepSpec}}
      allowAlternatingPatterns={allowAlternatingGridPatterns}
      shouldShowNullForEmptyContentsTooltip={shouldShowNullForEmptyContentsTooltip}
    />
  ),
  [LookupStepType.CHILD_ENTITIES]: (props: DummyFormProps) =>
    _childEntitySchemaStepConfigForm(
      props,
      'Child entity schemas',
      props.lookupInfo.parentSchema as TagSchema | null
    ),

  [LookupStepType.CONCENTRATION]: (props: DummyFormProps) => (
    <MeasurementUnitForm
      measurementType={MeasurementType.CONCENTRATION}
      disabled={props.disabled}
      isInline={props.isInline}
      label="Concentration units"
      value={(props.lookupInfo.lookupStep as ConcentrationLookupStepSpec).concentrationUnits}
      onUpdate={(value: string) => {
        props.onChange(
          {
            ...(props.lookupInfo.lookupStep as ConcentrationLookupStepSpec),
            concentrationUnits: value,
          },
          null
        );
      }}
      dataTestAttrs={getDropdownValueFormTestAttrs('concentrationUnits')}
    />
  ),
  [LookupStepType.CONSTANT]: (props: DummyFormProps) => (
    <TextField
      disabled={props.disabled}
      label="Value"
      onChange={(e) => {
        props.onChange(
          {
            ...(props.lookupInfo.lookupStep as ConstantLookupStepSpec),
            value: e.target.value,
          },
          null
        );
      }}
      labelStyle={FieldLabel.STYLES.LABEL}
      isRequired={true}
      value={`${(props.lookupInfo.lookupStep as ConstantLookupStepSpec).value}`}
      dataTestAttrs={{component: 'LookupStepForm', element: 'constantValue'}}
    />
  ),
  [LookupStepType.CONTAINER]: (props: DummyFormProps) =>
    _containerSchemaStepConfigForm(props, 'Container of entity'),
  [LookupStepType.CONTENTS]: (props: DummyFormProps) =>
    _containableSchemaStepConfigForm(props, 'Contents of container'),
  [LookupStepType.COUNT]: (props: DummyFormProps) => {
    const fieldOptions = [];
    const schema = props.lookupInfo.parentSchema;
    if (schema) {
      const selectedField = schema.allFields.find(
        (field) => field.apiId === (props.lookupInfo.lookupStep as CountLookupStepSpec).schemaField
      );
      if (selectedField && isSchemaFieldArchived(selectedField)) {
        const schemaFieldName = getArchiveItemName(getSchemaFieldArchivableItem(selectedField));
        fieldOptions.push({
          text: schemaFieldName,
          value: selectedField.apiId,
        });
      }

      const schemaFields = getSchemaFieldsForLookupStep(schema, isFirstStep, schemaFieldFilterFn);

      fieldOptions.push(
        ...schemaFields.map((field) => ({
          text: field.displayName,
          value: field.apiId,
        }))
      );
    }
    const formError = props.getErrorMessage('Field deleted', props.formErrors, 'dropdownValue');
    return (
      <DropdownValueForm
        disabled={props.disabled}
        isRequired={true}
        isInline={props.isInline}
        label="Schema field"
        value={(props.lookupInfo.lookupStep as CountLookupStepSpec).schemaField}
        onUpdate={(value: string) => {
          props.onChange(
            {
              ...(props.lookupInfo.lookupStep as CountLookupStepSpec),
              schemaField: value,
            },
            null
          );
        }}
        options={fieldOptions}
        dataTestAttrs={getDropdownValueFormTestAttrs('countField')}
        error={formError}
      />
    );
  },
  [LookupStepType.LOCATION]: (props: DummyFormProps) =>
    _locationSchemaStepConfigForm(props, 'Parent inventory location'),
  [LookupStepType.PLACEHOLDER_BOXES]: (props: DummyFormProps) =>
    _boxSchemaStepConfigForm(props, 'Box schema'),
  [LookupStepType.PLACEHOLDER_PLATES]: (props: DummyFormProps) =>
    _plateSchemaStepConfigForm(props, 'Plate schema'),
  [LookupStepType.PLATE]: (props: DummyFormProps) =>
    _plateSchemaStepConfigForm(props, 'Plate of container', true),
  [LookupStepType.TASK_FINDER_PROCESS_TASKS]: (props: DummyFormProps) => (
    <DropdownValueForm
      disabled={props.disabled}
      isRequired={true}
      isInline={props.isInline}
      label="Task schema field"
      value={(props.lookupInfo.lookupStep as TaskFinderProcessTasksLookupStepSpec).linkedBySchemaField}
      onUpdate={(value: string) => {
        props.onChange(
          {
            ...(props.lookupInfo.lookupStep as TaskFinderProcessTasksLookupStepSpec),
            linkedBySchemaField: value,
          },
          context?.processTaskSchema
        );
      }}
      options={
        context?.processTaskSchema
          ? context.processTaskSchema.allFields.map((field) => ({
              text: field.displayName,
              value: field.apiId,
            }))
          : []
      }
      dataTestAttrs={getDropdownValueFormTestAttrs('processTaskSchemaField')}
    />
  ),
  [LookupStepType.RESULTS]: (props: DummyFormProps) => {
    assert(previousStepInfo != null, 'Expected results step to have a previous step.');
    const itemType = getItemTypeForResultsStep(previousStepInfo);
    return _resultsSchemaStepConfigForm(props, itemType, schemaFieldFilterFn);
  },
  [LookupStepType.QUANTITY]: (props: DummyFormProps) => (
    <MeasurementUnitForm
      measurementType={MeasurementType.QUANTITY}
      disabled={props.disabled}
      isInline={props.isInline}
      label={`${getQuantityDisplayString()} units`}
      value={(props.lookupInfo.lookupStep as QuantityLookupStepSpec).volumeUnits}
      onUpdate={(value: string) => {
        props.onChange(
          {
            ...(props.lookupInfo.lookupStep as QuantityLookupStepSpec),
            volumeUnits: value,
          },
          null
        );
      }}
      dataTestAttrs={getDropdownValueFormTestAttrs('volumeUnits')}
    />
  ),
  [LookupStepType.WELLS]: (props: DummyFormProps) => (
    <WellsLookupStepConfigForm
      {...props}
      lookupInfo={props.lookupInfo as LookupStepInfo & {lookupStep: WellsLookupStepSpec}}
      allowAlternatingPatterns={allowAlternatingGridPatterns}
      shouldShowNullForEmptyContentsTooltip={shouldShowNullForEmptyContentsTooltip}
    />
  ),
  [LookupStepType.WELL_COORDINATES]: (props: DummyFormProps) => (
    <DropdownValueForm
      disabled={props.disabled}
      isRequired={false}
      isInline={props.isInline}
      label="Coordinate type"
      value={
        (props.lookupInfo.lookupStep as WellCoordinatesLookupStepSpec).coordinatesFillDirection ??
        (props.lookupInfo.lookupStep as WellCoordinatesLookupStepSpec).coordinatesType ??
        CoordinateType.LETTER_AND_NUMBER
      }
      onUpdate={(value: FillDirection | CoordinateType) => {
        if (Object.values(FillDirection).includes(value as FillDirection)) {
          props.onChange(
            {
              ...(props.lookupInfo.lookupStep as WellCoordinatesLookupStepSpec),
              coordinatesFillDirection: value as FillDirection,
              coordinatesType: null,
            },
            null
          );
        } else {
          props.onChange(
            {
              ...(props.lookupInfo.lookupStep as WellCoordinatesLookupStepSpec),
              coordinatesFillDirection: null,
              coordinatesType: value as CoordinateType,
            },
            null
          );
        }
      }}
      options={FILL_DIRECTIONS_AND_COORDINATE_TYPES_TO_VALUES}
      dataTestAttrs={getDropdownValueFormTestAttrs('coordinatesFillDirection')}
    />
  ),
  [LookupStepType.CONTAINER_BY_GRID_POSITION]: (props: DummyFormProps) => {
    const schema = props.lookupInfo.parentSchema;
    const formError = props.getErrorMessage(
      'Must select a position field',
      props.formErrors,
      'missingSecondaryField'
    );
    return (
      <DropdownValueForm
        label="Position field"
        value={(props.lookupInfo.lookupStep as ContainerByGridPositionLookupStepSpec).gridPositionSchemaField}
        options={
          schema
            ? getSchemaFieldsForLookupStep(schema, false, schemaFieldFilterFn)
                .filter((f) => f.fieldType === FieldType.TEXT)
                .map((f) => ({text: f.displayName, value: f.apiId}))
            : []
        }
        onUpdate={(value) => {
          props.onChange(
            {
              ...(props.lookupInfo.lookupStep as ContainerByGridPositionLookupStepSpec),
              gridPositionSchemaField: value!,
            },
            props.lookupInfo.linkSchema
          );
        }}
        dataTestAttrs={getDropdownValueFormTestAttrs('gridPositionSchemaField')}
        error={formError}
      />
    );
  },
});

const LOOKUP_STEP_ADDITIONAL_CONFIG_TYPES = new Set(
  Object.keys(lookupConfigStepForm({}, false, false, undefined, false, null))
);

export const isLookupStepConfigurable = (lookupStep: LookupStepSpec): boolean =>
  LOOKUP_STEP_ADDITIONAL_CONFIG_TYPES.has(lookupStep.type);

export interface LookupStepFormProps {
  disabled?: boolean;
  isInline?: boolean;
  lookupStepInfo: LookupStepInfo;
  previousStepInfo?: LookupStepInfo | null;
  handleSetLookupStep: (lookupStep: LookupStepInfo) => void;
  context?: LookupConfigModalContextInfo;
  processTaskSchema?: Schema | null;
  formErrors: FormErrors;
  isFirstStep: boolean;
  schemaFieldFilterFn?: (field: SchemaField, excludeNonLinkFields: boolean) => boolean;
  allowAlternatingGridPatterns?: boolean;
  shouldShowNullForEmptyContentsTooltip?: boolean;
  basePaneInvalidLookupObjectErrorMessage?: () => JSX.Element;
}

export default function LookupStepForm(props: LookupStepFormProps): JSX.Element | null {
  const stepType = props.lookupStepInfo.lookupStep.type;
  assert(
    props.isFirstStep || props.previousStepInfo != null,
    'Expected previous step info to be present for non-first step.'
  );
  const renderForm = lookupConfigStepForm(
    props.context,
    props.allowAlternatingGridPatterns ?? false,
    props.shouldShowNullForEmptyContentsTooltip ?? false,
    props.schemaFieldFilterFn,
    props.isFirstStep,
    props.previousStepInfo ?? null
  )[stepType];
  if (renderForm == null) {
    assert(false, `Expected to configure ${stepType} lookup step but form is not defined`);
    return null;
  }

  const getErrorMessage: boundGetErrorFn = (errorText, errors, errorKey) =>
    getLookupFormErrorMessage(errorText, errors, errorKey, props.basePaneInvalidLookupObjectErrorMessage);

  return renderForm({
    disabled: props.disabled,
    isInline: props.isInline,
    lookupInfo: props.lookupStepInfo,
    onChange: (lookupStep: LookupStepSpec, linkSchema: Schema | null) => {
      props.handleSetLookupStep({
        ...props.lookupStepInfo,
        lookupStep,
        linkSchema,
      });
    },
    formErrors: props.formErrors,
    getErrorMessage,
  });
}
